import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Box, Text, Icon, Flex, useToast } from '@chakra-ui/react';
import { FaSignOutAlt, FaWallet } from 'react-icons/fa';

// Import wallet adapter styles
import '@solana/wallet-adapter-react-ui/styles.css';

const CustomDisconnectButton = () => {
  const { connected, connecting, disconnect, select, wallets, wallet } = useWallet();
  const { setVisible, visible } = useWalletModal();
  const [isConnecting, setIsConnecting] = useState(false);
  const toast = useToast();

  // Check if a wallet extension is installed
  const isWalletAvailable = useCallback(() => {
    return !!(window?.solana?.isPhantom || window?.solflare?.isSolflare);
  }, []);

  // Handle connection status changes
  useEffect(() => {
    if (connecting) {
      setIsConnecting(true);
    } else if (connected) {
      setIsConnecting(false);
      // Successfully connected
      console.log(`Wallet connected in CustomDisconnectButton: ${wallet.adapter.name}`);
      // Store connection status
      localStorage.setItem('palmheist_wallet_connected', 'true');
    } else if (isConnecting && !connecting && !connected) {
      setIsConnecting(false);
      // Connection failed or was cancelled
      toast({
        title: "Connection Failed",
        description: "Failed to connect wallet. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [connecting, connected, isConnecting, toast]);

  // Handle wallet connection
  const handleConnect = useCallback(() => {
    // Check if a wallet extension is installed
    if (!isWalletAvailable()) {
      toast({
        title: "No Wallet Detected",
        description: "Please install a Solana wallet extension like Phantom or Solflare.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsConnecting(true);
    
    // If there's only one wallet available, select it automatically
    if (wallets.length === 1) {
      select(wallets[0].adapter.name);
    } else {
      // Otherwise show the wallet selection modal
      setVisible(true);
    }
  }, [select, setVisible, wallets, isWalletAvailable, toast]);

  // Handle click on the button
  const handleClick = () => {
    if (connected) {
      disconnect();
      toast({
        title: "Wallet Disconnected",
        description: "Your wallet has been disconnected.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } else {
      handleConnect();
    }
  };

  return (
    <Box
      as="button"
      bg={connected ? "#1E1E24" : "#1E1E24"}
      color={connected ? "#FF3A5E" : "#FF3A5E"}
      borderRadius="6px"
      height="32px"
      px={3}
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      onClick={handleClick}
      _hover={{
        bg: "#2A2A33"
      }}
      transition="all 0.2s"
      disabled={connecting || isConnecting}
    >
      <Flex align="center">
        {connected ? (
          <Icon as={FaSignOutAlt} mr={2} />
        ) : (
          <Icon as={FaWallet} mr={2} />
        )}
        <Text fontSize="sm" fontWeight="600">
          {connecting || isConnecting ? "Connecting..." : connected ? "Disconnect" : "Connect"}
        </Text>
      </Flex>
    </Box>
  );

};

export default CustomDisconnectButton;
